import { useState } from "react";
import { createPortal } from "react-dom";
import { BellRing, Check, X } from "lucide-react";
import { FONT, BTN_ORANGE, BTN_GHOST, MONO } from "./theme";

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return `${new Intl.DateTimeFormat("en-CA", {
      dateStyle: "medium", timeStyle: "short", timeZone: "America/Toronto",
    }).format(new Date(iso))} ET`;
  } catch {
    return iso;
  }
};

/**
 * NEW NOTE alert popup (spec item 7). Opens automatically when a job with
 * unread note versions is opened, and manually from the Estimate Home
 * badge. Acknowledgment is explicit ("I've read this") and per version —
 * showing the popup never marks anything read. When several versions are
 * unread, acknowledging one reveals the next; none are lost.
 */
export default function NoteAlertDialog({ open, onClose, projectName, versions, onAck }) {
  const [busy, setBusy] = useState(false);
  if (!open || !versions?.length) return null;
  const firstUnread = versions.findIndex((v) => !v.read);
  const v = versions[firstUnread === -1 ? versions.length - 1 : firstUnread];
  const remaining = versions.filter((x) => !x.read).length;
  const ack = async () => {
    setBusy(true);
    try {
      await onAck(v.version_id);
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/50 p-3 sm:items-center" data-testid="note-alert-overlay">
      <div style={FONT} className="w-full max-w-lg rounded-2xl bg-white shadow-2xl" data-testid="note-alert-dialog" role="dialog" aria-modal="true">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <div className={`flex items-center gap-1.5 ${v.read ? "text-slate-400" : "text-amber-600"}`}>
              <BellRing className="h-4 w-4 shrink-0" />
              <span className="text-[10px] font-bold uppercase tracking-widest" data-testid="note-alert-state">
                {v.read ? "New note — Read" : "New note — Must read"}
              </span>
            </div>
            <h3 className="mt-1 truncate text-base font-semibold text-slate-900" data-testid="note-alert-job">{projectName}</h3>
            <p className="mt-0.5 text-xs text-slate-500" data-testid="note-alert-meta">
              By {v.author || "unknown"} · {fmtWhen(v.saved_at)}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" data-testid="note-alert-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap px-5 py-4 text-sm leading-relaxed text-slate-800" data-testid="note-alert-content">
          {v.content}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-5 py-3.5">
          <span className={`${MONO} text-[11px] text-slate-400`} data-testid="note-alert-remaining">
            {remaining > 1 ? `${remaining} unread notes` : ""}
          </span>
          <div className="flex gap-2">
            {!v.read && (
              <button onClick={ack} disabled={busy} className={`${BTN_ORANGE} disabled:opacity-50`} data-testid="note-alert-ack">
                <Check className="mr-1.5 inline h-4 w-4" />{busy ? "Saving…" : "I've read this"}
              </button>
            )}
            <button onClick={onClose} className={BTN_GHOST} data-testid="note-alert-dismiss">
              {v.read ? "Close" : "Read later"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
